import * as React from 'react'

import { Dialog as DialogPrimitive } from 'radix-ui'

import { cn } from './cn'

export interface DialogProps {
  open: boolean
  onClose?: (
    event?: unknown,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => void
  children?: React.ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  fullScreen?: boolean
  className?: string
  /** Extra classes for the panel element. */
  PaperProps?: { className?: string; style?: React.CSSProperties }
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  /** Renders the panel as this element (e.g. "form"). */
  component?: React.ElementType
  onSubmit?: React.FormEventHandler
  /** Old MUI transition hook; onEntered fires when the dialog opens. */
  TransitionProps?: { onEntered?: () => void }
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

const maxWidthClasses = {
  xs: 'max-w-[444px]',
  sm: 'max-w-[600px]',
  md: 'max-w-[900px]',
  lg: 'max-w-[1200px]',
  xl: 'max-w-[1536px]',
}

const Dialog = ({
  open,
  onClose,
  children,
  maxWidth = 'sm',
  fullWidth,
  fullScreen,
  className,
  PaperProps,
  onKeyDown,
  component: Component = 'div',
  onSubmit,
  TransitionProps,
  ...aria
}: DialogProps) => {
  const onEntered = TransitionProps?.onEntered
  React.useEffect(() => {
    if (open && onEntered) {
      const t = setTimeout(onEntered, 0)
      return () => clearTimeout(t)
    }
    return undefined
  }, [open, onEntered])

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose?.()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[1300] bg-black/55 backdrop-blur-[2px] animate-ps-fade-in" />
        <DialogPrimitive.Content
          onKeyDown={onKeyDown}
          aria-labelledby={aria['aria-labelledby']}
          aria-describedby={aria['aria-describedby']}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'fixed z-[1300] flex flex-col overflow-hidden bg-paper text-text-primary shadow-dialog',
            'focus:outline-none animate-ps-zoom-in',
            fullScreen
              ? 'inset-0'
              : cn(
                  'left-1/2 top-1/2 max-h-[calc(100%-64px)] w-[calc(100%-64px)] -translate-x-1/2 -translate-y-1/2 rounded-modal',
                  maxWidth && maxWidthClasses[maxWidth],
                  !fullWidth && 'w-auto max-w-[calc(100%-64px)]',
                ),
            className,
            PaperProps?.className,
          )}
          style={PaperProps?.style}
          asChild={Component !== 'div'}
        >
          {Component !== 'div' ? (
            <Component onSubmit={onSubmit}>{children}</Component>
          ) : (
            children
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default Dialog

export const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & { component?: React.ElementType }
>(({ className, component: Component = 'h2', ...props }, ref) => (
  <Component
    ref={ref}
    className={cn(
      'shrink-0 px-6 pb-3 pt-5 text-lg font-semibold tracking-tight',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = 'DialogTitle'

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { dividers?: boolean }
>(({ className, dividers, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'min-h-0 grow overflow-y-auto px-6 py-2',
      dividers && 'border-y border-divider py-4',
      className,
    )}
    {...props}
  />
))
DialogContent.displayName = 'DialogContent'

export const DialogContentText = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('mb-3 text-base text-text-secondary', className)}
    {...props}
  />
))
DialogContentText.displayName = 'DialogContentText'

export const DialogActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex shrink-0 items-center justify-end gap-2 px-6 py-4',
      className,
    )}
    {...props}
  />
))
DialogActions.displayName = 'DialogActions'
